// #ux-redesign-start: RegisterPage rediseñada (Paso 5)
// Reemplaza el form Reactstrap/InputGroup/CardSignup por AuthLayout + AuthCard
// + primitivas del DS. Validación con react-hook-form v5 + yup, errores inline
// (sin toast spam por cada campo touched). Strength meter en password.
// AuthTerms encapsula el checkbox + modal de AGB.
//
// PORTABILIDAD: trasplantable a Web-Patients.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'react-translate';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import { actionRegister } from 'store/actions/usersActions';
import { Button, Input, PasswordField, toast } from 'components/ds';
import AuthLayout from 'components/Auth/AuthLayout';
import AuthCard from 'components/Auth/AuthCard';
import AuthTerms from 'components/Auth/AuthTerms';

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const emailRex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const RegisterPage = (props) => {
  const t = useTranslate('register-page');
  // #ux-redesign: comparte panel-title/panel-subtitle con el LoginPage
  const tLogin = useTranslate('login-page');
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userReducer);

  const userSchema = yup.object().shape({
    firstName: yup.string().min(2).required(t('error-required-firstName')),
    lastName: yup.string().min(2).required(t('error-required-lastName')),
    username: yup
      .string()
      .min(6)
      .matches(emailRex, t('error-required-username'))
      .required(t('error-required-username')),
    password: yup.string().min(6, t('error-required-password')).required(t('error-required-password')),
    rePassword: yup
      .string()
      .oneOf([yup.ref('password'), null], t('error-required-rePassword'))
      .required(t('error-required-rePassword')),
    terms: yup
      .boolean()
      .oneOf([true], t('accept-terms'))
      .required(t('accept-terms')),
  });

  const { register, handleSubmit, errors } = useForm({
    validationSchema: userSchema,
    mode: 'onBlur',
  });

  useEffect(() => {
    document.body.classList.add('register-page');
    return () => document.body.classList.remove('register-page');
  }, []);

  useEffect(() => {
    if (user.isAuth) {
      // Patients: post-registro va a completar el perfil (no hay dashboard).
      props.history.push('/admin/profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAuth]);

  const onSubmit = (data) => {
    if (!data.terms) {
      return toast.warning(t('accept-terms'));
    }
    dispatch(actionRegister({
      ...data,
      username: (data.username || '').toLowerCase().trim(),
    }));
  };

  const footerLinks = (
    <>
      <Link to="/auth/login-page">{t('login') || 'Anmelden'}</Link>
    </>
  );

  return (
    <Fragment>
      <AuthLayout
        panelTitle={tLogin('panel-title')}
        panelSubtitle={tLogin('panel-subtitle')}
      >
        <AuthCard title={t('register')} footerLinks={footerLinks}>
          <form className="ds-auth__form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              name="firstName"
              label={t('first-name')}
              placeholder={t('first-name')}
              iconLeft={<UserIcon />}
              autoComplete="given-name"
              innerRef={register}
              error={errors.firstName?.message}
            />
            <Input
              name="lastName"
              label={t('last-name')}
              placeholder={t('last-name')}
              iconLeft={<UserIcon />}
              autoComplete="family-name"
              innerRef={register}
              error={errors.lastName?.message}
            />
            <Input
              name="username"
              type="email"
              label={t('email')}
              placeholder={t('email')}
              iconLeft={<MailIcon />}
              autoComplete="email"
              innerRef={register}
              error={errors.username?.message}
            />
            <PasswordField
              name="password"
              label={t('password')}
              placeholder="••••••••"
              autoComplete="new-password"
              iconLeft={<LockIcon />}
              innerRef={register}
              error={errors.password?.message}
              showStrength
            />
            <PasswordField
              name="rePassword"
              label={t('re-password')}
              placeholder="••••••••"
              autoComplete="new-password"
              iconLeft={<LockIcon />}
              innerRef={register}
              error={errors.rePassword?.message}
            />
            <AuthTerms t={t} register={register} error={errors.terms?.message} />
            <div className="ds-auth__card-actions">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                name="register_button"
              >
                {t('get-started')}
              </Button>
            </div>
          </form>
        </AuthCard>
      </AuthLayout>
    </Fragment>
  );
};

export default RegisterPage;
// #ux-redesign-end
